"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Box, Compass, RefreshCw, Truck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const arenaCards = [{
  number: "01",
  title: "Operating Footprint",
  text: "The complete arena can require significantly more space than the mechanical base.",
  icon: Compass
}, {
  number: "02",
  title: "Transport",
  text: "Packed inflatable size matters to mobile operators.",
  icon: Truck
}, {
  number: "03",
  title: "Storage",
  text: "The inflatable needs its own storage planning when not in use.",
  icon: Box
}, {
  number: "04",
  title: "Replacement",
  text: "The inflatable may eventually need repair or replacement separately from the bull.",
  icon: RefreshCw
}];
export default function Arena() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="inflatablesafetyarena" data-nav="light" className="relative w-full border-b border-border bg-background py-20 text-foreground md:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-4xl">
          <h2 className="font-serif text-3xl font-bold uppercase tracking-tight sm:text-4xl md:text-5xl">
            Why the Inflatable Safety Arena Deserves Its Own Attention
          </h2>

          <div className="mt-6 space-y-4 border-l-2 border-primary pl-4 text-base leading-relaxed text-muted-foreground md:pl-6 md:text-lg">
            <p>
              The inflatable does more than change the appearance of the setup. It affects the total operating footprint, transportation, setup time, storage and the complete package price.
            </p>
            <p>
              For buyers who move equipment between locations, the inflatable can also influence how much space the system takes when packed and how much handling is required at each setup.
            </p>
            <p>
              For a permanent venue, the more important question may be whether the complete arena fits comfortably in the intended operating space.
            </p>
          </div>
        </div>

        {/* Industrial Four Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {arenaCards.map((card, i) => {
          const CardIcon = card.icon;
          return <motion.div key={card.number} variants={itemVariants} data-index={i} className="h-full">
                <Card className="flex h-full flex-col justify-between rounded-none border border-border bg-card p-6 shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/60 hover:shadow-md">
                  <div>
                    <CardHeader className="p-0">
                      <div className="flex items-center justify-between border-b border-border/80 pb-4">
                        <span className="font-mono text-sm font-semibold tracking-widest text-primary">
                          [{card.number}]
                        </span>
                        <div className="flex size-9 items-center justify-center border border-border bg-muted/40 text-foreground">
                          <CardIcon className="size-4" />
                        </div>
                      </div>
                      <CardTitle className="pt-4 font-mono text-sm font-bold uppercase tracking-wider text-card-foreground">
                        {card.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="p-0 pt-3">
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        {card.text}
                      </p>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Action / Context Strip */}
        <div className="mt-12 flex flex-col items-start justify-between gap-4 border border-border bg-muted/30 p-5 sm:flex-row sm:items-center sm:p-6">
          <div className="space-y-1">
            <div className="font-mono text-xs font-semibold uppercase tracking-wider text-primary">
              Facility & Transport Clearance
            </div>
            <p className="text-sm text-muted-foreground">
              Review full spatial dimensions and blower power requirements in the setup guidelines.
            </p>
          </div>
          <Button asChild variant="outline" className="rounded-none border-border font-mono text-xs uppercase tracking-wider hover:border-foreground">
            <Link to="/SetupSafetyAndMaintenancePage" className="inline-flex items-center gap-2">
              Setup specs
              <ArrowUpRight className="size-3.5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}